import * as React from "react";
import { useLocation } from "wouter";
import { Target, ListChecks, PackageSearch, ArrowRight, ArrowLeft } from "lucide-react";

/**
 * "Give things a home" chooser — the three on-ramps to the same "where should this
 * go?" outcome (deciding where a thing belongs, including items with no home yet).
 * Sits under the Placement category (/organize/placement). Each card routes to an
 * existing destination; this page only exists to keep the drill-down shallow (one
 * clear question per screen) instead of a wall of intents.
 *   - Find a home         → item-first  → /organize/goals?new=1&type=find_home
 *   - Sort out a room     → camera-first (triage) → /organize/triage
 *   - Unplaced & Re-locate → list-first → /organize/lists
 */
type OnRamp = {
  key: string;
  label: string;
  hint: string;
  icon: React.ElementType;
  iconClass: string;
  assistive?: boolean;
  href: string;
};

const ONRAMPS: OnRamp[] = [
  { key: "find_home", label: "Find a home", hint: "Pick specific things and decide where they should live", icon: Target, iconClass: "text-primary", href: "/organize/goals?new=1&type=find_home" },
  { key: "triage", label: "Sort out a room", hint: "Scan a space and sort what's there, one thing at a time", icon: ListChecks, iconClass: "text-sage-deep", assistive: true, href: "/organize/triage" },
  { key: "lists", label: "Unplaced & Re-locate", hint: "Things with no home, and things you flagged to move", icon: PackageSearch, iconClass: "text-primary", href: "/organize/lists" },
];

export default function FindHomesPage() {
  const [, navigate] = useLocation();

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <header>
        <button
          onClick={() => navigate("/organize/placement")}
          className="inline-flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors mb-2"
        >
          <ArrowLeft className="w-4 h-4" />Placement
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Target className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium text-primary">Give things a home</span>
        </div>
        <h1 className="text-4xl font-semibold text-ink font-headline tracking-[-0.02em]">Where should it go?</h1>
        <p className="text-body mt-1 text-sm">Three ways to start — pick whichever fits what you're holding.</p>
      </header>

      <div className="space-y-3">
        {ONRAMPS.map((r) => {
          const Icon = r.icon;
          return (
            <a
              key={r.key}
              href={r.href}
              className="flex items-center gap-3 bg-white rounded-lg border border-clay-border p-4 hover:border-primary/40 transition-colors group"
            >
              <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${r.assistive ? "bg-sage-wash" : "bg-amethyst-soft"}`}>
                <Icon className={`w-5 h-5 ${r.iconClass}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[1.0625rem] font-semibold text-ink">{r.label}</div>
                <div className="text-[0.8125rem] text-faint">{r.hint}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-faint group-hover:text-primary transition-colors shrink-0" />
            </a>
          );
        })}
      </div>
    </div>
  );
}
